// Lib
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { length } from '../../../../../common/utils/immutableHelper';
import { getColorDisplayValue } from '../../../../../common/colors/elementColorFormatUtil';
import { getThemeIsDarkMode } from '../../../../theme/themeSelector';
import { getBackgroundColorHex } from '../../bridge/utils/dataUtils/cell/tableCellStyleUtils';
import { setHotTableCellBackgroundColor } from '../../bridge/utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import { selectActiveTableCurrentCellBackgroundColor } from '../store/selectors/activeHotTableSelectors';
import { selectActiveTableCellSelection } from '../../bridge/store/selectors/activeHotTableCellSelectionSelectors';
import { updateHotTableSelectedCellBackgroundColor } from '../../bridge/utils/hotInstanceUtils/cell/hotTableCellUpdateUtils';

// Components
import ToolbarPopupTool from '../../../../workspace/toolbar/components/ToolbarPopupTool';
import DesktopColorPopup from '../../../../workspace/toolbar/components/selectionTools/colorTools/popup/desktop/DesktopColorPopup';
import ColorToolIcon from '../../../../workspace/toolbar/components/selectionTools/colorTools/ColorToolIcon';
import { useActiveTableCallback, useActiveTableSelector } from '../../bridge/store/hotTableStoreHooks';

// Constants
import { PopupIds, PopupSections } from '../../../../components/popupPanel/popupConstants';
import {
    TABLE_DEFAULT_COLORS,
    TABLE_DEFAULT_HEADER_BACKGROUND_COLOR,
} from '../../../../../common/table/tableConstants';
import { CellSelections } from '../../../../../common/table/TableTypes';
import { ImMNElement } from '../../../../../common/elements/elementModelTypes';

// Styles
import './TableCellColorTool.scss';

type ColorToolPopupIconProps = {
    selectedColor: string | null | undefined;
    isPopupOpen: boolean;
    currentCellSelections: CellSelections;
};

const ColorToolPopupIcon: React.FC<ColorToolPopupIconProps> = ({
    selectedColor,
    isPopupOpen,
    currentCellSelections,
}) => {
    const color =
        currentCellSelections?.length > 0
            ? getColorDisplayValue(selectedColor)
            : 'var(--ui-toolbar-tool-disabled-foreground)';
    const selectedColorDisplayValue =
        selectedColor === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR ? 'var(--ws-table-header-row-background)' : color;

    return <ColorToolIcon color={selectedColorDisplayValue} isActive={isPopupOpen} />;
};

type TableCellColorToolProps = {
    element?: ImMNElement;
};

const TableCellColorTool: React.FC<TableCellColorToolProps> = (props) => {
    const isDarkMode = useSelector(getThemeIsDarkMode);
    const currentCellSelections = useActiveTableSelector(selectActiveTableCellSelection);

    const selectedColor = useActiveTableSelector(selectActiveTableCurrentCellBackgroundColor);

    // Temporary picker previews mutate HOT cell meta directly, so we track that state and force the
    // final committed update to persist even when the chosen color matches the already-previewed value.
    const hasTempColorRef = useRef(false);

    const updateCellBackgroundColor = useActiveTableCallback((activeTable, backgroundColor: string | null) => {
        updateHotTableSelectedCellBackgroundColor(activeTable.instance, backgroundColor, hasTempColorRef.current);
        hasTempColorRef.current = false;
    }, []);

    const updateTemporaryCellColor = useActiveTableCallback((activeTable, tempColor: string | null) => {
        setHotTableCellBackgroundColor(activeTable.instance, undefined, tempColor);
        hasTempColorRef.current = true;
    }, []);

    return (
        <ToolbarPopupTool
            {...props}
            name="Cell color"
            buttonSelector=".ColorTool .ColorToolIcon"
            popupId={PopupIds.COLOR}
            closeSection={PopupSections.TOOLBAR}
            toolName="color"
            disabled={length(currentCellSelections) === 0}
            // @ts-ignore Struggling with the JSX type inference
            className="table-cell-color-tool"
            customColorHandler={updateCellBackgroundColor}
            IconComponent={ColorToolPopupIcon}
            PopupComponent={DesktopColorPopup}
            selectedColor={selectedColor}
            selectedColorHex={getBackgroundColorHex(selectedColor, isDarkMode)}
            customDefaultColors={Object.values(TABLE_DEFAULT_COLORS)}
            customTempColorHandler={updateTemporaryCellColor}
            currentCellSelections={currentCellSelections}
            preventFocus
        />
    );
};

export default TableCellColorTool;
